/**
 * FE mirror of the gateway's `UserInfoSchema` + sender-block codec.
 *
 * The Argus gateway prepends a single-line HTML-comment block to every
 * inbound user message so the agent knows who sent it. We parse the same
 * block back out on the FE to render an author chip per message, then
 * strip it from the visible body.
 *
 * Keep the wire format in lockstep with `be/argus-ts/src/argus-context.ts`.
 */

export interface ArgusUserInfo {
  source: string;
  id: string;
  name?: string;
  email?: string;
}

// ---------------------------------------------------------------------------
// Clerk → ArgusUserInfo
// ---------------------------------------------------------------------------

/**
 * Structural subset of Clerk's `UserResource` — the only fields we read.
 * Kept narrow to avoid a dependency on `@clerk/types` (not installed
 * directly; Clerk ships its types via `@clerk/clerk-react`).
 */
interface ClerkUserLike {
  id: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  primaryEmailAddress?: { emailAddress?: string | null } | null;
}

function deriveDisplayName(clerk: ClerkUserLike): string | undefined {
  const full = clerk.fullName?.trim();
  if (full) return full;
  const parts = [clerk.firstName, clerk.lastName].filter(
    (p): p is string => typeof p === "string" && p.length > 0,
  );
  if (parts.length > 0) return parts.join(" ");
  return clerk.username ?? undefined;
}

/**
 * Build an ArgusUserInfo from a Clerk `useUser()` result.
 *
 * Returns `null` when the user isn't signed in — callers then simply skip
 * attaching a sender block, matching the gateway's "user is optional"
 * contract.
 */
export function getArgusUserInfoFromClerk(
  clerkUser: ClerkUserLike | null | undefined,
): ArgusUserInfo | null {
  if (!clerkUser) return null;
  const name = deriveDisplayName(clerkUser);
  const email = clerkUser.primaryEmailAddress?.emailAddress ?? undefined;
  return {
    source: "clerk",
    id: clerkUser.id,
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
  };
}

// ---------------------------------------------------------------------------
// Sender-block parser (mirrors be/argus-ts/src/argus-context.ts)
// ---------------------------------------------------------------------------

const SENDER_BLOCK_RE = /^<!-- argus:sender ([^\n]*?) -->\n\n?/;
const SENDER_ATTR_RE = /(\w+)="([^"]*)"/g;

function unescapeSenderAttr(value: string): string {
  return value.replaceAll("&quot;", '"').replaceAll("-- >", "-->");
}

/**
 * Strip and parse a leading `<!-- argus:sender ... -->` block. Safe to call
 * on any string — if no block is present, returns `{ sender: null, body:
 * content }` unchanged.
 */
export function parseSenderBlock(content: string): {
  sender: ArgusUserInfo | null;
  body: string;
} {
  const match = SENDER_BLOCK_RE.exec(content);
  if (!match) return { sender: null, body: content };

  const attrs: Record<string, string> = {};
  for (const [, key, value] of match[1].matchAll(SENDER_ATTR_RE)) {
    attrs[key] = unescapeSenderAttr(value);
  }
  if (!attrs.source || !attrs.id) {
    return { sender: null, body: content };
  }
  const sender: ArgusUserInfo = {
    source: attrs.source,
    id: attrs.id,
    ...(attrs.name ? { name: attrs.name } : {}),
    ...(attrs.email ? { email: attrs.email } : {}),
  };
  return { sender, body: content.slice(match[0].length) };
}

// ---------------------------------------------------------------------------
// Chip-rendering helpers
// ---------------------------------------------------------------------------

function firstGrapheme(s: string): string {
  // Good-enough for initials: avoids surrogate-pair issues for most scripts.
  return [...s][0] ?? "";
}

export function getSenderInitials(sender: ArgusUserInfo): string {
  const source = sender.name?.trim() || sender.email?.trim() || "";
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (firstGrapheme(parts[0]) + firstGrapheme(parts[parts.length - 1])).toUpperCase();
  }
  return firstGrapheme(source).toUpperCase();
}

export function getSenderDisplayName(sender: ArgusUserInfo): string {
  return sender.name?.trim() || sender.email?.trim() || "Unknown";
}
